import { SetStateAction } from "react";

import { AxiosResponse } from "axios";
import { TFunction } from "i18next";
import _ from "lodash";
import { ScheduleDto } from "@/components/DetailsSection/types";
import { TRANSLATABLE_FILTER_VALUES } from "@/components/Filters/types/enums";

import { ApiResources } from "../../api/resources";
import { ALERT_STATUS } from "../../context/alert/types";
import { ENVIRONMENTS } from "../../routes/types";
import {
  Company,
  INPUT_LENGTH,
  LANGUAGES,
  LOCAL_STORAGE_KEYS,
  RefreshTableAndUpdateDetailsProps,
  ROUTES,
  USER_INFO_LENGTHS
} from "..";

export const getValidLanguage = (lang: any, defaultLang: LANGUAGES) => {
  const isValid = Object.values(LANGUAGES).includes(lang);

  return isValid ? (lang as LANGUAGES) : defaultLang;
};

export const setStoredLanguage = (language: string) =>
  localStorage.setItem(LOCAL_STORAGE_KEYS.lng, language);

export const getStoredLanguage = () =>
  localStorage.getItem(LOCAL_STORAGE_KEYS.lng);

export const deleteStoredLanguage = () =>
  localStorage.removeItem(LOCAL_STORAGE_KEYS.lng);

export const parseResource = (name: string, data: any): string | null =>
  data[name];

export const generateColor = (
  inverted: boolean | undefined,
  firstcolor: string,
  secondColor: string
) => (inverted ? firstcolor : secondColor);

export const capitalizeResourceFirstLetter = (string: string) => {
  return string.charAt(1).toUpperCase() + string.slice(2);
};

export const removeSlashFromPath = (path: string) => path.replace("/", "");

export const capitalizeStringFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
export const capitalizeStringOnlyFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};
export const capitalizeLocationFirstLetter = (string: string) => {
  return string.charAt(1).toUpperCase() + string.slice(2);
};

export const isDriverPayloadReadyToConfirm = (
  cardHolderName: string | null,
  cardHolderNameIsValid: boolean | string | null,
  driverId: string | null,
  inputIsValid: () => boolean,
  descriptionIsValid: boolean
) =>
  cardHolderName &&
  cardHolderNameIsValid &&
  driverId &&
  inputIsValid() &&
  descriptionIsValid;

export const descriptionIsValid = (descriptionValue: string | null) =>
  (descriptionValue && descriptionValue.length <= INPUT_LENGTH.Description) ||
  !descriptionValue;

export const cardHolderNameIsValid = (cardHolderName: string | null) =>
  cardHolderName &&
  cardHolderName.length <= INPUT_LENGTH.CardHolderName &&
  cardHolderName.length >= INPUT_LENGTH.MinCardHolderName;

export const checkIfDataTypeIsDisabled = (
  givenDataType: string,
  schedule: any
) => {
  let result;
  const givenFileTypes = schedule?.fileTypes || schedule;

  if (givenFileTypes?.includes(givenDataType)) {
    result = false;
  } else {
    result = true;
  }
  return result;
};

export const numberRegex = /\d+/;
export const letterRegex = /\D/g;

export const getNumberFromString = (givenString: string) =>
  givenString?.replace(letterRegex, "");

export const getStringWithoutNumber = (givenString: string) =>
  givenString?.replace(numberRegex, "");

export const passwordLengthIsValid = (password: string | null) =>
  (password && password.length <= USER_INFO_LENGTHS.PswMax) || !password;

export const routesWithoutDetails = (pathname: string) =>
  pathname === ROUTES.VehicleFiles ||
  pathname === ROUTES.DriverFiles ||
  pathname === ROUTES.Dashboard;

export const replaceStringWithSpacesToCamelCase = (string: string) => {
  let result: string = "";
  string.split(" ").forEach((str: string) => {
    result = result + str[0].toUpperCase() + str.slice(1);
  });
  return result;
};

export const replaceCamelCaseStringToSeparateWords = (camelcase: string) => {
  return camelcase.replace(/([a-z](?=[A-Z]))/g, "$1 ");
};

export const replaceCamelCaseStringToRegularSentence = (camelcase: string) => {
  return capitalizeStringOnlyFirstLetter(
    replaceCamelCaseStringToSeparateWords(camelcase).toLowerCase()
  );
};

export const refreshTableAndUpdateDetails = async (
  parameters: RefreshTableAndUpdateDetailsProps
): Promise<void> => {
  const {
    valueInSearchField,
    resourceId,
    successMessage,
    itemHasOpenedDetails,
    getTableItemDetails,
    handleResponse,
    leaveRowsChecked
  } = parameters;

  getTableItemDetails({
    itemId: resourceId,
    successMessage: successMessage,
    leaveTableDetailsNotUpdated: !itemHasOpenedDetails,
    updateTableRowParams: true,
    fromEditItem: true,
    valueInSearchField: valueInSearchField ?? undefined,
    leaveRowsChecked: leaveRowsChecked
  });

  if (valueInSearchField && successMessage) {
    handleResponse(ALERT_STATUS.Success, successMessage);
  }
};

export const getEnvironment = () => {
  const env = import.meta.env.VITE_ENVIRONMENT;

  return {
    env: env,
    isProductionEnv: env === ENVIRONMENTS.Production,
    isDevelopmentEnv: env === ENVIRONMENTS.Development,
    isStagingEnv: env === ENVIRONMENTS.Staging
  };
};

export const isValidNumberValue = (value: string) => {
  const regexNumber = /^[0-9\b]+$/;

  return value === "" || regexNumber.test(value);
};

export const findIndexOfArrayElement = (array: any[], element: any) => {
  return array.indexOf(element);
};

export const sendCodeToEmail = async (
  setLoading: (value: SetStateAction<boolean>) => void,
  successCallback: () => void,
  postData: (
    resource: string,
    payload?: any
  ) => Promise<AxiosResponse<any, any>>,
  handleCommonError: (
    error: any,
    t: TFunction<"translation", undefined>
  ) => void,
  t: TFunction<"translation", undefined>
) => {
  try {
    setLoading(true);
    await postData(ApiResources.CreateTwoFactorTokenEmail);
    successCallback();
  } catch (error) {
    handleCommonError(error, t);
  }

  setLoading(false);
};

export const isAnalyticsCookieAccepted = (cookieConsentStatus: any) =>
  cookieConsentStatus.analytics === "true";

export const sortCompanies = (
  companies: Company[],
  skipFirst: boolean
): Company[] => {
  if (!skipFirst) {
    return _.sortBy(companies, company => company.name.toLowerCase());
  }

  if (companies.length <= 1) return companies;

  const [first, ...rest] = companies;
  const sortedRest = _.sortBy(rest, company => company.name.toLowerCase());

  return [first, ...sortedRest];
};
export const getScheduleTitle = (t: TFunction, schedule: ScheduleDto) => {
  if (schedule.global || schedule.quick) {
    return t(`Table##${schedule.name}`);
  }

  return schedule.name;
};

export const generateTranslatedText = (
  itemName: string,
  i18n: any,
  t: TFunction
) => {
  const translatableValues = Object.values(
    TRANSLATABLE_FILTER_VALUES
  ) as string[];

  if (!translatableValues.includes(itemName)) {
    return itemName;
  }

  const translationKey = [`General##${itemName}`, `Table##${itemName}`].find(
    key => i18n.exists(key)
  );

  return translationKey ? t(translationKey) : t(`Filter##${itemName}`);
};
